import moment from "moment";
import { useBusSchedulesQuery } from "src/services/react-query-apis/get-bus-schedules";
import createStudentLink from "src/utils/createStudentLink";
import deepCopy from "src/utils/deepCopy";
import { getJpWeekDay } from "src/utils/formatdate";
import * as XLSX from "xlsx";

function exportBusSchedule(header, tableHeader, tableBodyData, filename) {
  const data = [
    [header], // Title row to be merged across 6 columns
    tableHeader,
    ...tableBodyData,
  ];

  // Convert to worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  // Add merge info
  worksheet["!merges"] = [
    // Merge top title across 6 columns (A1:F1)
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
  ];

  // Set column widths (in characters)
  worksheet["!cols"] = [
    { wch: 20 }, // バス停
    { wch: 20 }, // 名前
    { wch: 20 }, // フリガナ
    { wch: 5 }, // 往
    { wch: 5 }, // 復
    { wch: 20 }, // 備考
  ];

  // Create and save workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "バス");
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

function RidingListTable({ date, busScheduleId, attendances }) {
  const busScheduleQuery = useBusSchedulesQuery({
    month: moment(date).format("YYYY-MM"),
    queryConfig: {
      enabled: !!busScheduleId,
    },
  });

  const busSchedule = busScheduleQuery.data?.data?.find(
    busSchedule => busSchedule.id === Number(busScheduleId)
  );

  const routes = busSchedule?.bus_course?.bus_routes
    .map(route => {
      const riderAttendances = attendances.filter(attendance => {
        return (
          attendance.outbound_bus_route?.id === route.id ||
          attendance.return_bus_route?.id === route.id
        );
      });

      return {
        route: route,
        riderAttendances: deepCopy(riderAttendances),
      };
    })
    .filter(route => route.riderAttendances.length > 0);

  const attendanceTotalPassengers = (attendance, prefix) => {
    return attendance[`bus_schedule_id`] !== busScheduleId
      ? 0
      : attendance[`${prefix}_passengers`];
  };

  const routeTotalPassengers = (routeRiderAttendances, prefix) => {
    return routeRiderAttendances.reduce((total, rider) => {
      return total + attendanceTotalPassengers(rider, prefix);
    }, 0);
  };

  const totalPassengers = prefix => {
    return routes?.reduce((total, route) => {
      return total + routeTotalPassengers(route.riderAttendances, prefix);
    }, 0);
  };

  const tableHeader = ["バス停", "名前", "フリガナ", "往", "復", "備考"];

  const tableBodyData = [];

  routes?.forEach(routeData => {
    const busRoute = routeData.route;
    const stopName = busRoute.stop_name;
    const busStopTime = busRoute.formatted_bus_stop_time;

    let firstCellContent = {
      0: stopName,
      1: busStopTime,
    };

    routeData.riderAttendances.forEach((attendance, index, arr) => {
      const member = attendance.member_classes.member;
      const comment = attendance.comment;

      const outboundPassengers = attendanceTotalPassengers(
        attendance,
        "outbound"
      );
      const returnPassengers = attendanceTotalPassengers(attendance, "return");

      const passengerString = passengers => {
        if (passengers === 0) return "";
        if (passengers === 1) return "〇";
        return "◎";
      };

      if (arr.length === 1) {
        tableBodyData.push([
          firstCellContent[0],
          createStudentLink(member.name, member.id),
          member.furigana,
          passengerString(outboundPassengers),
          passengerString(returnPassengers),
          comment,
        ]);

        tableBodyData.push([firstCellContent[1], "", "", "", "", ""]);
      } else {
        tableBodyData.push([
          firstCellContent[index],
          createStudentLink(member.name, member.id),
          member.furigana,
          passengerString(outboundPassengers),
          passengerString(returnPassengers),
          comment,
        ]);
      }
    });

    tableBodyData.push([
      "",
      "",
      "",
      routeTotalPassengers(routeData.riderAttendances, "outbound"),
      routeTotalPassengers(routeData.riderAttendances, "return"),
      "",
    ]);

    tableBodyData.push(["", "", "", "", "", ""]);
  });

  tableBodyData.push(["", "", "", "往", "復", ""]);
  tableBodyData.push([
    "",
    "",
    "",
    totalPassengers("outbound"),
    totalPassengers("return"),
    "",
  ]);

  return (
    <div className="table-responsive" id="ridingTable">
      <div className="p-3 mb-3 light-border">
        <div className="bg-light p-2">
          <div className="row ride_headline">
            <h5>{busSchedule?.description}</h5>
          </div>
        </div>
        <div className="table-responsive">
          <table className="mb-0 table table-bordered mt-2 custom-tbl2 align-middle">
            <thead style={{ marginTop: "10px" }}>
              <tr>
                {tableHeader.map((header, index, arr) => (
                  <th
                    key={index}
                    className={`text-center ${
                      index === arr.length - 1 ? "w-25" : ""
                    }`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody style={{ fontSize: "15px" }}>
              {tableBodyData.map((row, index) => (
                <tr key={index}>
                  {row.map((cell, index) => (
                    <td
                      key={index}
                      className={`${[3, 4].includes(index) ? "text-center" : ""}`}
                    >
                      {cell === "" ? <div>&nbsp;</div> : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <button
          className="btn btn-primary"
          onClick={() => {
            const filename = `${date}-${busSchedule?.description}`;
            const header = `${busSchedule?.description}`;
            exportBusSchedule(header, tableHeader, tableBodyData, filename);
          }}
        >
          ダウンロード
        </button>
      </div>
    </div>
  );
}

export default RidingListTable;
